/**
 * A machine in words, for its page: how it is reached (one readout), whose it is, and the policy it follows. No ids
 * on the main surfaces; the Details disclosure in Settings keeps those for administrators.
 */

import type { FleetDevice } from '@aioe/shared';
import { useEffect, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { hasDirectory, resolveSubjects, subjectName, subscribeNames } from '../directory.ts';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';

type Mark = 'connected' | 'online' | 'offline';

/** One readout: connected (and how), online but not taking remote control, or offline since when. */
export function connectionOf(d: Pick<FleetDevice, 'online' | 'lastSeenAt'> & { relayConnected?: boolean } | undefined, via: 'mesh' | 'relay' | undefined): { state: Mark; label: string; detail: string } {
  if (!d) return { state: 'offline', label: 'Checking', detail: '' };
  if (d.relayConnected) return { state: 'connected', label: 'Connected', detail: via === 'mesh' ? 'over the private network' : 'over the relay' };
  if (d.online) return { state: 'online', label: 'Online', detail: 'not taking remote control right now' };
  return { state: 'offline', label: 'Offline', detail: `last seen ${timeAgo(d.lastSeenAt)}` };
}

/** The person a desktop belongs to, by name when the directory knows it, else their email. "You" for the reader. */
export function useOwnerWords(d: Pick<FleetDevice, 'userId' | 'userEmail' | 'kind' | 'hostname'> | undefined): string | undefined {
  const { account } = useAuth();
  const id = d?.userId;
  const mine = Boolean(id && account?.id === id);
  const s = useSyncExternalStore(subscribeNames, () => (id ? subjectName(id) : undefined));
  useEffect(() => {
    if (id && !mine && hasDirectory()) void resolveSubjects([id]);
  }, [id, mine]);
  if (!d) return undefined;
  if (mine) return 'Yours';
  const name = s && !s.missing && s.name !== id ? s.name : d.userEmail;
  return name ? `${d.kind === 'node' ? 'Sponsored by' : 'Belongs to'} ${name}` : undefined;
}

/** `default@6+enterprise+teams:61+…`: the policy record and its revision; the rest are what the answer folds in. */
export function parsePolicyRevision(rev: string | undefined): { id: string; revision: number } | undefined {
  const m = /^([^@+]+)@(\d+)/.exec(rev ?? '');
  return m ? { id: m[1]!, revision: Number(m[2]) } : undefined;
}

/** "Follows the policy “Engineering” (revision 6)", the name a link to the policy for those who may open it. */
export function PolicyLine({ revision, admin }: { revision: string | undefined; admin: boolean }) {
  const p = parsePolicyRevision(revision);
  const policies = useLoad(() => (admin && p ? routes.policies().catch(() => undefined) : Promise.resolve(undefined)), [admin, p?.id]);
  if (!p) return <span>It has not fetched a policy yet.</span>;
  const found = policies.data?.find((x) => x.id === p.id);
  const href = `/policies/${encodeURIComponent(p.id)}`;
  if (found)
    return (
      <span>
        Follows the policy{' '}
        <Link to={href} className="font-medium text-accent hover:underline">
          “{found.name}”
        </Link>{' '}
        (revision {p.revision}).
      </span>
    );
  if (p.id === 'unpublished') return <span>Follows the defaults: no policy has been published for it yet.</span>;
  return (
    <span>
      Follows {admin && !isCloud() ? <Link to={href} className="font-medium text-accent hover:underline">its organisation’s policy</Link> : 'its organisation’s policy'} (revision {p.revision}).
    </span>
  );
}
